import { useId } from 'react'
import type { ExifExtractorSettings } from '@/data/dropPlugins'

const FIELD_GROUPS = [
  { key: 'location' as const, label: 'Location', description: 'GPS coordinates and altitude' },
  { key: 'dateTime' as const, label: 'Date & Time', description: 'When the photo was taken' },
  { key: 'cameraInfo' as const, label: 'Camera Info', description: 'Camera make, model, and lens' },
  { key: 'shootingSettings' as const, label: 'Shooting Settings', description: 'ISO, aperture, shutter speed, focal length' },
] as const

interface ExifSettingsProps {
  settings: ExifExtractorSettings
  onChange: (settings: ExifExtractorSettings) => void
  disabled?: boolean
  bare?: boolean
}

export function ExifSettings({ settings, onChange, disabled, bare }: ExifSettingsProps) {
  const id = useId()
  const fieldGroups = settings.fieldGroups ?? {}

  const onToggle = (key: keyof NonNullable<ExifExtractorSettings['fieldGroups']>, checked: boolean) => {
    const current = settings.fieldGroups ?? {}
    const updated = { ...current, [key]: checked }

    // If all groups match defaults (location+dateTime=true, others=false), clear the object
    const isDefault =
      (updated.location ?? true) === true &&
      (updated.dateTime ?? true) === true &&
      !updated.cameraInfo &&
      !updated.shootingSettings

    if (isDefault) {
      onChange({})
    } else {
      onChange({ fieldGroups: updated })
    }
  }

  const content = (
    <div className="space-y-3">
      {FIELD_GROUPS.map((group) => {
          const checked = fieldGroups[group.key] ?? (group.key === 'location' || group.key === 'dateTime')
          const inputId = `${id}-${group.key}`
          return (
            <label key={group.key} htmlFor={inputId} className="flex items-start gap-3 cursor-pointer">
              <input
                id={inputId}
                type="checkbox"
                checked={checked}
                onChange={(e) => onToggle(group.key, e.target.checked)}
                disabled={disabled}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
              />
              <div>
                <div className="text-sm font-medium">{group.label}</div>
                <div className="text-xs text-muted-foreground">{group.description}</div>
              </div>
            </label>
          )
        })}
    </div>
  )

  if (bare) return content

  return (
    <div className="bg-card text-card-foreground rounded-lg border p-6 space-y-6">
      <div>
        <h3 className="text-sm font-semibold mb-1">EXIF Metadata</h3>
        <p className="text-xs text-muted-foreground">Choose which photo metadata to preserve from uploads</p>
      </div>
      {content}
    </div>
  )
}
